/**
 * Content Card Types for Admin Panel
 * Matches the backend data models from the CDK stack
 */

export const SUPPORTED_LANGUAGES = ['en', 'hr', 'it', 'de', 'es', 'fr', 'sk', 'sv', 'sl', 'cs'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const CARD_TYPES = [
  'DALMATIAN_WORD',
  'NEWS',
  'TIP',
  'FACT',
  'ANNOUNCEMENT',
  'VIDEO',
  'GALLERY',
  'SHORT_STORY',
] as const;
export type CardType = typeof CARD_TYPES[number];

export const CARD_TYPE_LABELS: Record<CardType, string> = {
  DALMATIAN_WORD: 'Dalmatian Word',
  NEWS: 'News',
  TIP: 'Tip',
  FACT: 'Fact',
  ANNOUNCEMENT: 'Announcement',
  VIDEO: 'Video',
  GALLERY: 'Gallery',
  SHORT_STORY: 'Short Story',
};

export const CARD_STATUSES = ['DRAFT', 'SCHEDULED', 'PUBLISHED', 'ARCHIVED'] as const;
export type CardStatus = typeof CARD_STATUSES[number];

// Translation status types for async translation pipeline
export const TRANSLATION_STATUSES = ['TRANSLATING', 'TRANSLATED', 'PARTIAL', 'FAILED', 'NONE'] as const;
export type TranslationStatus = typeof TRANSLATION_STATUSES[number];

// Generation status types for async content generation (Requirement 10.2)
export const GENERATION_STATUSES = ['GENERATING', 'GENERATED', 'FAILED', 'NONE'] as const;
export type GenerationStatus = typeof GENERATION_STATUSES[number];

export type LanguageTranslationStatus = 'pending' | 'completed' | 'failed';

/** One key per target language of the card: 9 for an English source, `en` plus 8 for a Croatian source (VAP-827). */
export type TranslationProgress = Partial<Record<SupportedLanguage, LanguageTranslationStatus>>;

export type TranslationRetryCount = Partial<Record<SupportedLanguage, number>>;

// Target languages for translation (excludes English which is the source)
export const TARGET_LANGUAGES = ['hr', 'it', 'de', 'es', 'fr', 'sk', 'sv', 'sl', 'cs'] as const;
export type TargetLanguage = typeof TARGET_LANGUAGES[number];

/** Languages an admin can write a card in (VAP-827). The rest are translated from it. */
export const SOURCE_LANGUAGES = ['en', 'hr'] as const;
export type SourceLanguage = typeof SOURCE_LANGUAGES[number];
export const SOURCE_LANGUAGE_LABELS: Record<SourceLanguage, string> = {
  en: 'English',
  hr: 'Croatian',
};

/** The language the card was written in; old cards have no field and were written in English. */
export function sourceLanguageOf(card: { sourceLanguage?: SourceLanguage }): SourceLanguage {
  return card.sourceLanguage ?? 'en';
}

/** The card text in the language it was written in. */
export function sourceContentOf(card: Pick<ContentCard, 'content' | 'sourceLanguage'>) {
  return card.content[sourceLanguageOf(card)];
}

export const LANGUAGE_NAMES: Record<TargetLanguage | 'en', string> = {
  en: 'English',
  hr: 'Croatian',
  it: 'Italian',
  de: 'German',
  es: 'Spanish',
  fr: 'French',
  sk: 'Slovak',
  sv: 'Swedish',
  sl: 'Slovenian',
  cs: 'Czech',
};

export interface LocalizedString {
  en: string;
  hr: string;
  it: string;
  de: string;
  es: string;
  fr: string;
  sk: string;
  sv: string;
  sl: string;
  cs: string;
}

export interface MediaItem {
  type: 'image' | 'youtube';
  url: string;
  thumbnailUrl?: string;
  altText?: LocalizedString;
}

export interface ExternalLink {
  url: string;
  label: LocalizedString;
}

export interface CardMetrics {
  fetchCount: number;
  likeCount: number;
  averageRating: number;
  ratingCount: number;
}

export interface DalmatianWordData {
  word: string;
  translations: LocalizedString;
}

export interface ContentCard {
  cardId: string;
  cardType: CardType;
  status: CardStatus;
  publishDate: string;
  expirationDate?: string;
  isPushFlagged: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  content: {
    [key in SupportedLanguage]?: {
      title: string;
      summary: string;
      body: string;
    };
  };
  /** Language the admin wrote the card in (VAP-827). Absent on old cards = English. */
  sourceLanguage?: SourceLanguage;
  mediaUrls: MediaItem[];
  externalLinks: ExternalLink[];
  metrics: CardMetrics;
  dalmatianWord?: DalmatianWordData;
  // Image attachment fields (Requirement 9.6)
  imageUrl?: string;
  imageKey?: string;
  // Async translation pipeline fields
  translationStatus?: TranslationStatus;
  translationProgress?: TranslationProgress;
  translationRetryCount?: TranslationRetryCount;
  // Async generation pipeline fields (Requirement 10.2)
  generationStatus?: GenerationStatus;
  generationPrompt?: string;
  generationError?: string;
}

export interface CreateContentRequest {
  cardType: CardType;
  title: string;
  summary?: string;
  body: string;
  mediaUrls?: MediaItem[];
  externalLinks?: Array<{ url: string; label: string }>;
  publishDate: string;
  expirationDate?: string;
  isPushFlagged?: boolean;
  generateTranslations?: boolean;
  /** Language of title/summary/body: 'en' (default) or 'hr' (VAP-827) */
  sourceLanguage?: SourceLanguage;
}

export interface UpdateContentRequest {
  cardType?: CardType;
  title?: string;
  summary?: string;
  body?: string;
  mediaUrls?: MediaItem[];
  externalLinks?: Array<{ url: string; label: string }>;
  publishDate?: string;
  /** null clears the expiration date */
  expirationDate?: string | null;
  isPushFlagged?: boolean;
  regenerateTranslations?: boolean;
  /** Language of title/summary/body; the Lambda refuses with 409 when it differs from the card's (VAP-827) */
  sourceLanguage?: SourceLanguage;
  /** Croatian translation override - allows manual correction of Croatian content */
  croatianContent?: {
    title?: string;
    summary?: string;
    body?: string;
  };
  /** Image URL for the content card (Requirement 9.6) - set to null to remove image */
  imageUrl?: string | null;
}

export interface GenerateContentRequest {
  cardType: CardType;
  publishDate: string;
  prompt?: string;
  generateImage?: boolean;
}

export interface BulkDeleteRequest {
  cardIds: string[];
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

export interface ContentListResponse {
  cards: ContentCard[];
  count: number;
  nextToken?: string;
}
